
import { OptionTypeEnum } from "./option-type.class";
import { TimePeriod, TimePeriodData } from "../time-period.class";
import { OptionConfigClass } from "./option-config.class";
import { MutltipleChoiceList, MutltipleChoiceListPersistence } from "./mulitple-choice/multiple-choice-list.class";
import cloneDeep from 'lodash/cloneDeep';
import assign from 'lodash/assign'
import { MutltipleChoiceData } from "./mulitple-choice/multiple-choice.class";

/**  Representing the configuration of an option. */
export interface OptionData {
    index?: number; /**should never change, this is the link to the vote; the order in which the option is saved, */
    optionConfigKey?: string /** defines the optionConfig */
    type?: OptionTypeEnum; /** @depricated historically defining TEXT or DATE option, now replaced by the optionConfig*/
    label?: string; /**actually the text titel */
    secondaryAnswerMax?: number;
    timePeriod?: TimePeriodData;
    description?: string;
    maxNumberOfEntries?: number;
    checked?: boolean; /** determines if an option was selected by the user , attribute should not persist*/
    isDeleted?: boolean;
    updatedAt?: any;
    createdAt?: any;
    createdByUid?: string;
    createdByName?: string;
    isCreatedByParticipant?: boolean;
}

export interface OptionPersistence extends OptionData {
    multipeChoice?: MutltipleChoiceListPersistence
}



export class OptionClass {
    private _optionData: OptionData;
    private _mcOptionList: MutltipleChoiceList;
    private _timeZone: string;
    private _timePeriodClass: TimePeriod;

    constructor(optionPersistence: OptionPersistence, timeZone: string) {
        this._timeZone = timeZone
        if (!optionPersistence) { throw new Error('!optionData') }
        if (!optionPersistence.type) { throw new Error('!optionData.type') }
        if (!optionPersistence.optionConfigKey) { // for old options that have on configKey
            if (optionPersistence.type === OptionTypeEnum.date) {
                optionPersistence.optionConfigKey = 'date'
            } else {
                optionPersistence.optionConfigKey = 'text'
            }
        }
        this._optionData = <OptionData>optionPersistence
        if (optionPersistence.type === OptionTypeEnum.date) {
            this._optionData.timePeriod = optionPersistence.timePeriod
        }
        this.initMultipleChoiceList(optionPersistence?.multipeChoice?.dataArray, optionPersistence?.multipeChoice?.sortMap)
    }

    private initMultipleChoiceList(dataArray?: MutltipleChoiceData[], sortMap?: number[]) {
        this._mcOptionList = new MutltipleChoiceList(dataArray, sortMap)
    }

    get mutltipleChoiceList(): MutltipleChoiceList {
        return this._mcOptionList
    }

    /**
     * copies a deep clone
     */
    set mutltipleChoiceList(mcList: MutltipleChoiceList) {
        this._mcOptionList = mcList.cloneDeep();
    }

    get data(): OptionPersistence {
        if (this._timePeriodClass) this._optionData.timePeriod = this._timePeriodClass.timePeriodData;
        const optionPersistence = cloneDeep(this._optionData)
        optionPersistence.multipeChoice = cloneDeep(this.mutltipleChoiceList.persistenceData)
        return optionPersistence
    }

    get index(): number | undefined {
        return this._optionData.index;
    }
    set index(value: number | undefined) {
        this._optionData.index = value;
    }

    toString(): string {
        if (this.isOfQuestionTypeDate) return this.timePeriodClass.toString()
        if (this.isOfQuestionTypeText) return this.label
        if (this.isOfQuestionTypeTextAndNumber) return this.secondaryAnswerMax + " " + this.label
    }

    /**
     * contains a reference of the same data hence it can be used to manupulate the daa
     */
    get timePeriodClass(): TimePeriod {
        if (!this._timePeriodClass) {
            this._timePeriodClass = TimePeriod.newFromTimePeriodData(this._optionData.timePeriod);
        }
        return this._timePeriodClass
    }
    public static newOption(optionConfigKey: string, timeZone: string, timePeriod?: TimePeriod): OptionClass {
        const optionData: OptionData = { optionConfigKey: optionConfigKey };
        const optionConfig = OptionConfigClass.newOptionConfigClass(optionConfigKey)
        if (optionConfig.isOfTypeDate) {
            if (timePeriod == null) {
                timePeriod = TimePeriod.newWithDefaultData()
            }

            optionData.type = OptionTypeEnum.date
            optionData.timePeriod = timePeriod.timePeriodData
            return new OptionClass(optionData, timeZone);
        } else if (optionConfig.isOfTypeText) {
            optionData.type = OptionTypeEnum.text
            return new OptionClass(optionData, timeZone);
        } else if (optionConfig.isOfTypeTextAndNumber) {
            optionData.type = OptionTypeEnum.textAndNumber
            return new OptionClass(optionData, timeZone);
        } else {
            throw new Error('No question type')
        }
    }

    public deepCopy(): OptionClass {
        let returnValue = cloneDeep(this)
        return returnValue;
    }

    public copyAtrributes(option: OptionClass) {
        assign(this, option);
        console.log('copyAttributes', option, this)
    }

    /**
    * put the text-options first (ordered by optionIndex); then the date-options (ordered by date)
    * @param optionClassA
    * @param optionClassB
    * @returns
    */
    public static standardCompare(optionClassA: OptionClass, optionClassB: OptionClass): 1 | 0 | -1 {
        if (optionClassA.optionConfig.isOfTypeText && optionClassB.optionConfig.isOfTypeText) {
            if (optionClassA.index < optionClassB.index) {
                return -1
            } else {
                return 1
            }
        }
        if (optionClassA.optionConfig.isOfTypeText && optionClassB.optionConfig.isOfTypeDate) {
            return -1
        }
        if (optionClassA.optionConfig.isOfTypeDate && optionClassB.optionConfig.isOfTypeText) {
            return 1
        }
        // at this point both are of type date
        return TimePeriod.compareStartDateTime(optionClassA.timePeriodClass, optionClassB.timePeriodClass)
    }

    get isOfQuestionTypeDate(): boolean {
        return this.optionConfig.isOfTypeDate
    }

    get isOfQuestionTypeText(): boolean {
        return this.optionConfig.isOfTypeText
    }

    get isOfQuestionTypeTextAndNumber(): boolean {
        return this.optionConfig.isOfTypeTextAndNumber
    }

    get isMultipleChoice(): boolean {
        return this.optionConfig.isMultipleChoice
    }
    get optionConfigKey(): string {
        return this._optionData.optionConfigKey
    }

    set optionConfigKey(value: string) {
        this._optionData.optionConfigKey = value
    }

    get optionConfig(): OptionConfigClass {
        return OptionConfigClass.newOptionConfigClass(this._optionData.optionConfigKey)
    }


    get label(): string | undefined {
        return this._optionData.label;
    }
    set label(value: string | undefined) {
        this._optionData.label = value;
    }

    get secondaryAnswerMax(): number | undefined {
        return this._optionData.secondaryAnswerMax;
    }
    set secondaryAnswerMax(value: number) {
        this._optionData.secondaryAnswerMax = value;
    }

    get description(): string | undefined {
        return this._optionData.description;
    }

    set description(value: string | undefined) {
        this._optionData.description = value;
    }

    get maxNumberOfEntries(): number | undefined {
        return this._optionData.maxNumberOfEntries;
    }

    set maxNumberOfEntries(value: number | undefined) {
        this._optionData.maxNumberOfEntries = value;
    }


    get checked(): boolean | undefined {
        return this._optionData.checked;
    }

    set checked(value: boolean | undefined) {
        this._optionData.checked = value;
    }

    get isDeleted(): boolean | undefined {
        return this._optionData.isDeleted;
    }

    set isDeleted(value: boolean | undefined) {
        this._optionData.isDeleted = value;
    }

    get updatedAt(): any {
        return this._optionData.updatedAt;
    }

    set updatedAt(value: any) {
        this._optionData.updatedAt = value;
    }

    get createdAt(): any {
        return this._optionData.createdAt;
    }

    set createdAt(value: any) {
        this._optionData.createdAt = value;
    }


    public setMarkAsCreatedBy(name: string, uid: string) {
        this._optionData.isCreatedByParticipant = true;
        this._optionData.createdByName = name;
        this._optionData.createdByUid = uid;
    }

    get createdByUid(): string | undefined {
        return this._optionData.createdByUid;
    }

    get createdByName(): string | undefined {
        return this._optionData.createdByName;
    }

    set createdByName(name: string) {
        this._optionData.createdByName = name;
    }
    get isCreatedByParticipant(): boolean {
        if (this._optionData?.isCreatedByParticipant) return this._optionData?.isCreatedByParticipant;
        return false
    }
}
